import type { IconRef } from '@shared/ui/icons'

import { useAppBrand } from '@shared/lib/app-brand'

import { LessonRoomGuideCategory } from '../model/types/lesson-room/enums'
import { LESSON_ROOM_GUIDES_SMARTESTMINDS } from './lesson-room-guides-smartestminds'
import winScreen from '../ui/images/manual/android/screen.png'
import winPrivacy from '../ui/images/manual/android/privacy.png'
import winCamera from '../ui/images/manual/android/camera.png'
import winPrograms from '../ui/images/manual/android/programs.png'
import macScreen from '../ui/images/manual/windows/screen.png'
import macCmd from '../ui/images/manual/windows/cmd.png'
import macAllow from '../ui/images/manual/windows/allow.png'
import macCamera from '../ui/images/manual/windows/camera.png'
import macSettings from '../ui/images/manual/windows/settings.png'
import macDevice from '../ui/images/manual/windows/device.png'
import macLater from '../ui/images/manual/windows/later.png'
import macMicrophone from '../ui/images/manual/windows/microphone.png'
import macReopen from '../ui/images/manual/windows/reopen.png'
import ipadScreen from '../ui/images/manual/macbook/screen.png'
import ipadAllow from '../ui/images/manual/macbook/allow.png'
import ipadPermission from '../ui/images/manual/macbook/permission.png'
import ipadAccess from '../ui/images/manual/macbook/access.png'
import ipadIpad from '../ui/images/manual/macbook/ipad.png'
import ipadPrivacy from '../ui/images/manual/macbook/privacy.png'
import ipadChrome from '../ui/images/manual/macbook/chrome.png'
import ipadCamera from '../ui/images/manual/macbook/camera.png'
import ipadApps from '../ui/images/manual/macbook/apps.png'
import ipadMicrophone from '../ui/images/manual/macbook/microphone.png'
import androidSettings from '../ui/images/manual/ipad/settings.png'
import androidPermission from '../ui/images/manual/ipad/permission.png'
import androidCamera from '../ui/images/manual/ipad/camera.png'
import androidClose from '../ui/images/manual/ipad/close.png'
import androidRefresh from '../ui/images/manual/ipad/refresh.png'

export interface LessonRoomGuideStep {
  text: string
  image?: string
}

export interface LessonRoomGuideStepImage {
  avif: string
  webp: string
  width: number
  height: number
}

export interface LessonRoomGuideSectionStep {
  text: string
  image?: LessonRoomGuideStepImage
}

export interface LessonRoomGuideSection {
  label: string
  steps: LessonRoomGuideSectionStep[]
}

interface LessonRoomGuideBase {
  category: LessonRoomGuideCategory
  icon: IconRef
  label: string
  title: string
}

export interface LessonRoomGuideFlat extends LessonRoomGuideBase {
  steps: LessonRoomGuideStep[]
}

export interface LessonRoomGuideSectioned extends LessonRoomGuideBase {
  sections: LessonRoomGuideSection[]
}

export type LessonRoomGuide = LessonRoomGuideFlat | LessonRoomGuideSectioned

const TITLE = '❓ If your cam & mic permissions are blocked, try this:'

export const LESSON_ROOM_GUIDES: Record<LessonRoomGuideCategory, LessonRoomGuideFlat> = {
  [LessonRoomGuideCategory.Windows]: {
    category: LessonRoomGuideCategory.Windows,
    icon: { name: 'windows' },
    label: 'Windows laptop/PC',
    title: TITLE,
    steps: [
      { text: 'Click Start and open Settings', image: winScreen },
      { text: 'Click Privacy & security', image: winPrivacy },
      { text: 'Click Camera and click ON to allow access to your camera', image: winCamera },
      { text: 'Find your browser in the list of programs and make sure it is enabled', image: winPrograms },
      { text: 'Then do the same for the microphone' },
    ],
  },
  [LessonRoomGuideCategory.Macbook]: {
    category: LessonRoomGuideCategory.Macbook,
    icon: { name: 'apple' },
    label: 'Macbook/iMac',
    title: TITLE,
    steps: [
      { text: 'If you see this screen', image: macScreen },
      { text: 'Press the key combination Command + R', image: macCmd },
      { text: 'After the page reloads, click "Allow" in the permissions pop-up', image: macAllow },
      { text: 'Check if there is such an icon at the end of the URL line. If so, click on it.', image: macCamera },
      { text: 'Then click Open Settings', image: macSettings },
      { text: 'After opening the settings, click on checkbox near the browser icon, then "allow”', image: macDevice },
      { text: 'Then click “later”', image: macLater },
      { text: 'Then go to the microphone item click on checkbox near the browser icon', image: macMicrophone },
      { text: 'Press Quit & Reopen', image: macReopen },
      { text: 'After Chrome has opened again, open the link to the demo lesson again. Then turn on Camera & Mic and Join meeting' },
    ],
  },
  [LessonRoomGuideCategory.IphoneIpad]: {
    category: LessonRoomGuideCategory.IphoneIpad,
    icon: { name: 'apple' },
    label: 'iPhone/iPad',
    title: TITLE,
    steps: [
      { text: 'If you see this screen. Click on the “Reload” icon at the end of the URL line', image: ipadScreen },
      { text: 'After the page reloads, click "Allow" in the permissions pop-up', image: ipadAllow },
      { text: 'If you see this screen. Click on the “Reload” icon before the URL line', image: ipadPermission },
      { text: 'After the page reloads, click "Allow" in the permissions pop-up', image: ipadAccess },
      { text: 'If this does not help, open the Settings of your iPad', image: ipadIpad },
      { text: 'Then select the Privacy & Security section; Find and click on the Microphone item', image: ipadPrivacy },
      { text: 'Find Chrome in the apps list and enable the option. After that, go to the previous Privacy & Security page.', image: ipadChrome },
      { text: 'Find and click on the Camera item', image: ipadCamera },
      { text: 'Find Chrome in the apps list and enable the option.', image: ipadApps },
      { text: 'You can now reopen Chrome. Reload the page if it doesn\'t reload on its own. After the page reloads, click "Allow" or “OK” in the permissions pop-up', image: ipadMicrophone },
    ],
  },
  [LessonRoomGuideCategory.Android]: {
    category: LessonRoomGuideCategory.Android,
    icon: { name: 'android' },
    label: 'Android phone or tablet',
    title: TITLE,
    steps: [
      { text: 'If you see this screen. Click on the settings icon at the beginning of the URL line.', image: androidSettings },
      { text: 'In the window that appears, select Permissions', image: androidPermission },
      { text: 'Then turn on the Camera and Microphone permissions.', image: androidCamera },
      { text: 'Close the window.', image: androidClose },
      { text: 'Click on the “Reload” icon before the URL line Enjoy the lesson', image: androidRefresh },
    ],
  },
}

export const LESSON_ROOM_GUIDE_ORDER: LessonRoomGuideCategory[] = [
  LessonRoomGuideCategory.Windows,
  LessonRoomGuideCategory.Macbook,
  LessonRoomGuideCategory.IphoneIpad,
  LessonRoomGuideCategory.Android,
]

export const getLessonRoomGuide = (category: LessonRoomGuideCategory): LessonRoomGuide => {
  const { isSmartestMinds } = useAppBrand()

  return isSmartestMinds ? LESSON_ROOM_GUIDES_SMARTESTMINDS[category] : LESSON_ROOM_GUIDES[category]
}